// selectionOps.ts — pure operations on a SELECTION of the board: a set of cells picked by box-select,
// ⇧-click or ⌘A, which may take in empty cells as well as items. Move, delete, copy and paste all read the
// selection the same way:
//   * the ITEMS it holds are the items on its cells, grown to whole items — a gate is all of its cells, a
//     linked box brings its partner (moveGroupCells, the rule the one-item drag already follows);
//   * a MODIFIER is carried whole when every cell it covers is in the selection (a 3×3 lock needs all nine
//     cells boxed, which is why the selection keeps empty cells). One only partly inside follows the
//     one-item rule instead (modifierAfterMove): a link's ends go with their boxes, an area stays put.
// Every op returns the SAME template when it changes nothing, so the page's no-op guards keep working.
import { findCell, modifierAfterMove, modifierCovers, moveGroupCells, withCellRemoved, withGateClearedAt } from "./templateOps";
import { CellModifierType } from "../Models/CellModifier";
export function cellKey(x, y) {
    return `${x},${y}`;
}
function keysOf(cells) {
    return new Set(cells.map((c) => cellKey(c.x, c.y)));
}
function inBounds(t, x, y) {
    return x >= 0 && y >= 0 && x < t.columns && y < t.rows;
}
// Reading order (row by row, left to right) — how a selection is listed and which item leads it.
function byReadingOrder(a, b) {
    return a.y - b.y || a.x - b.x;
}
// The cells of every board cell from `a` to `b` inclusive, corners in any order, clipped to the board.
export function cellsInRect(t, a, b) {
    const out = [];
    for (let y = Math.max(0, Math.min(a.y, b.y)); y <= Math.min(t.rows - 1, Math.max(a.y, b.y)); y++) {
        for (let x = Math.max(0, Math.min(a.x, b.x)); x <= Math.min(t.columns - 1, Math.max(a.x, b.x)); x++) {
            out.push({ x, y });
        }
    }
    return out;
}
// Every cell of the board — ⌘A.
export function allCells(t) {
    return cellsInRect(t, { x: 0, y: 0 }, { x: t.columns - 1, y: t.rows - 1 });
}
// The item cells a selection holds, grown to whole items (a gate's full span, a link's partner), in reading
// order. Grown to a fixed point, since a partner brought in can itself belong to something larger.
export function selectedItemCells(t, region) {
    const seen = new Set();
    const out = [];
    const queue = region.filter((c) => findCell(t, c.x, c.y));
    while (queue.length > 0) {
        const c = queue.pop();
        for (const g of moveGroupCells(t, c.x, c.y)) {
            const k = cellKey(g.x, g.y);
            if (!seen.has(k) && findCell(t, g.x, g.y)) {
                seen.add(k);
                out.push(g);
                queue.push(g);
            }
        }
    }
    return out.sort(byReadingOrder);
}
// The board cells a modifier covers, plus the points it lists (which may lie off the board in a hand-edited
// file — such a point is never in a selection, so that modifier is never carried whole).
function footprint(t, m) {
    const out = new Set();
    if (!Array.isArray(m.cells)) {
        return out;
    }
    for (const p of m.cells) {
        if (p)
            out.add(cellKey(p.x, p.y));
    }
    for (let y = 0; y < t.rows; y++) {
        for (let x = 0; x < t.columns; x++) {
            if (modifierCovers(m, x, y))
                out.add(cellKey(x, y));
        }
    }
    return out;
}
// Whether the modifier lies wholly inside `cells` — then it goes wherever the selection goes.
function carriedWhole(t, m, cells) {
    if (!m || !Array.isArray(m.cells) || m.cells.length === 0) {
        return false;
    }
    const fp = footprint(t, m);
    for (const k of fp) {
        if (!cells.has(k))
            return false;
    }
    return fp.size > 0;
}
function shifted(m, dx, dy) {
    return { ...m, cells: m.cells.map((p) => ({ x: p.x + dx, y: p.y + dy })) };
}
// A modifier dropped where another of its kind already covers a cell. Two locks over one box, or two seals
// on one box, are states the game has no answer for; links are exempt (a box can carry two).
function clashes(t, placed, staying) {
    for (const m of placed) {
        if (m.type === CellModifierType.Connected)
            continue;
        const fp = footprint(t, m);
        for (const o of staying) {
            if (!o || o.type !== m.type)
                continue;
            for (const k of footprint(t, o)) {
                if (fp.has(k))
                    return true;
            }
        }
    }
    return false;
}
// Moves everything the selection holds by (dx, dy). Refused (null) when an item would leave the board or
// land on an item that is not moving, when a modifier carried whole would hang off the board, or when it
// would land on another of its kind. The returned region is the selection where it now sits, taking in the
// cells of whole items it grew to, so the next nudge carries exactly the same things.
export function moveSelection(t, region, dx, dy) {
    if (dx === 0 && dy === 0) {
        return null;
    }
    const items = selectedItemCells(t, region);
    const itemKeys = keysOf(items);
    const moving = new Set([...keysOf(region), ...itemKeys]);
    const mods = t.cells.modifiers;
    const whole = mods.map((m) => carriedWhole(t, m, moving));
    if (items.length === 0 && !whole.some(Boolean)) {
        return null; // nothing to move — an empty box-select
    }
    for (const c of items) {
        const nx = c.x + dx;
        const ny = c.y + dy;
        if (!inBounds(t, nx, ny) || (findCell(t, nx, ny) && !itemKeys.has(cellKey(nx, ny)))) {
            return null;
        }
    }
    const nextMods = mods.map((m, i) => (whole[i] ? shifted(m, dx, dy) : m ? modifierAfterMove(m, itemKeys, dx, dy) : m));
    if (nextMods.some((m, i) => whole[i] && m.cells.some((p) => !inBounds(t, p.x, p.y)))) {
        return null;
    }
    if (clashes(t, nextMods.filter((_, i) => whole[i]), nextMods.filter((_, i) => !whole[i]))) {
        return null;
    }
    const template = {
        ...t,
        cells: {
            items: t.cells.items.map((c) => (c && itemKeys.has(cellKey(c.x, c.y)) ? { ...c, x: c.x + dx, y: c.y + dy } : c)),
            modifiers: nextMods,
        },
    };
    const next = [];
    for (const k of moving) {
        const [x, y] = k.split(",").map(Number);
        if (inBounds(t, x + dx, y + dy))
            next.push({ x: x + dx, y: y + dy });
    }
    return { template, region: next.sort(byReadingOrder) };
}
// Removes everything the selection holds: its items (a gate goes whole) and every modifier touching one of
// its cells — the rule Delete already follows for a single cell, spread over the selection.
export function deleteSelection(t, region) {
    const items = selectedItemCells(t, region);
    const cells = new Set([...keysOf(region), ...keysOf(items)]);
    let out = t;
    for (const c of items) {
        const cell = findCell(out, c.x, c.y);
        if (cell)
            out = cell.isGate ? withGateClearedAt(out, c.x, c.y) : withCellRemoved(out, c.x, c.y);
    }
    const kept = out.cells.modifiers.filter((m) => {
        if (!m || !Array.isArray(m.cells))
            return true;
        for (const k of footprint(t, m)) {
            if (cells.has(k))
                return false;
        }
        return true;
    });
    if (kept.length !== out.cells.modifiers.length) {
        out = { ...out, cells: { ...out.cells, modifiers: kept } };
    }
    return out;
}
export function copySelection(t, region) {
    const items = selectedItemCells(t, region);
    const all = [...region, ...items];
    const cells = keysOf(all);
    const mods = t.cells.modifiers.filter((m) => carriedWhole(t, m, cells));
    if (items.length === 0 && mods.length === 0) {
        return null;
    }
    const minX = Math.min(...all.map((c) => c.x));
    const minY = Math.min(...all.map((c) => c.y));
    const maxX = Math.max(...all.map((c) => c.x));
    const maxY = Math.max(...all.map((c) => c.y));
    return {
        // A deep copy: the per-spawn records are arrays, and a pasted tunnel must never share them.
        items: items.map((c) => {
            const cell = structuredClone(findCell(t, c.x, c.y));
            return { ...cell, x: c.x - minX, y: c.y - minY };
        }),
        modifiers: mods.map((m) => structuredClone(shifted(m, -minX, -minY))),
        region: [...cells].map((k) => k.split(",").map(Number)).map(([x, y]) => ({ x: x - minX, y: y - minY })).sort(byReadingOrder),
        width: maxX - minX + 1,
        height: maxY - minY + 1,
    };
}
// Drops the clipboard with its top-left at `at` — pulled back inside the board when the cursor sits too near
// the right or bottom edge for it to fit. Refused (null) when any pasted item lands on an occupied cell or
// off the board, or a pasted modifier lands on another of its kind.
export function pasteClipboard(t, clip, at) {
    const ax = Math.max(0, Math.min(at.x, t.columns - clip.width));
    const ay = Math.max(0, Math.min(at.y, t.rows - clip.height));
    const items = clip.items.map((c) => ({ ...structuredClone(c), x: c.x + ax, y: c.y + ay }));
    if (items.some((c) => !inBounds(t, c.x, c.y) || findCell(t, c.x, c.y))) {
        return null;
    }
    const mods = clip.modifiers.map((m) => structuredClone(shifted(m, ax, ay)));
    if (mods.some((m) => m.cells.some((p) => !inBounds(t, p.x, p.y)))) {
        return null;
    }
    if (clashes(t, mods, t.cells.modifiers)) {
        return null;
    }
    return {
        template: { ...t, cells: { items: [...t.cells.items, ...items], modifiers: [...t.cells.modifiers, ...mods] } },
        region: clip.region.map((c) => ({ x: c.x + ax, y: c.y + ay })).filter((c) => inBounds(t, c.x, c.y)),
    };
}
// A copy of the selection set down beside it: to the right, else below, left, above — and failing all of
// those, the first place on the board it fits, in reading order. Null when it fits nowhere.
export function duplicateSelection(t, region) {
    const clip = copySelection(t, region);
    if (!clip) {
        return null;
    }
    const all = [...region, ...selectedItemCells(t, region)];
    const minX = Math.min(...all.map((c) => c.x));
    const minY = Math.min(...all.map((c) => c.y));
    const beside = [
        { x: minX + clip.width, y: minY },
        { x: minX, y: minY + clip.height },
        { x: minX - clip.width, y: minY },
        { x: minX, y: minY - clip.height },
    ];
    // Only the spots where the copy fits as it is: pasteClipboard pulls an overhanging anchor back inside,
    // which next to the original would drop the copy on top of it.
    const fits = (c) => c.x >= 0 && c.y >= 0 && c.x + clip.width <= t.columns && c.y + clip.height <= t.rows;
    for (const at of [...beside.filter(fits), ...allCells(t)]) {
        const r = pasteClipboard(t, clip, at);
        if (r)
            return r;
    }
    return null;
}
